import { trigger, transition, style, animate } from '@angular/animations';
import { AfterViewInit, Component, computed, effect, EventEmitter, inject, Input, input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { faChevronsRight } from '@fortawesome/pro-duotone-svg-icons';
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { faClose, faCode, faCodeBranch, faComment, faEye, faFileDashedLine, faGear, faPlus, faSquarePollHorizontal } from '@fortawesome/pro-regular-svg-icons';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { SnackbarMode } from '@eiq/ui';

import { Comment } from '@empathy/comments/data';
import { CommentsListComponent } from '@empathy/comments/feature';
import { ToPlainText } from '@empathy/global/functions';
import { BlockType, PreviewDevice, QuestionPrefix, QuestionType, Source, SourceVariables } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { SideOptionTabs } from './../../models/side-option-tabs.enum';
import { BlockTypePickerComponent } from '../../components/blocks/block-type-picker/block-type-picker.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { DeleteQuestionDialogComponent } from '../../components/dialogs/delete-question-dialog/delete-question-dialog.component';
import { DuplicateQuestionsDialogComponent } from '../../components/dialogs/duplicate-questions-dialog/duplicate-questions-dialog.component';
import { MoveBulkToPositionComponent } from '../../components/question-position/move-bulk-to-position/move-bulk-to-position.component';
import { QuestionTypePickerComponent } from '../../components/question-type-picker/question-type-picker.component';
import { SourcePickerComponent } from '../../components/sources-configuration/source-picker/source-picker.component';
import { MainTabs } from '../../models/main-tabs.enum';
import { OptionsLibrarySet } from '../../models/options-library/options-library-form.interface';
import { DemographicsGroup } from '../../models/question-type-config.constants';
import { SideHiddenTabs } from '../../models/side-hidden-tabs.enum';
import { SideTabs } from '../../models/side-tabs.enum';
import { SurveyEditor } from '../../models/survey-editor/survey-editor.model';
import { EditorTranslationsService } from '../../services/editor-translations.service';
import { OptionsLibraryService } from '../../services/options-library.service';
import { QuestionsLibraryService } from '../../services/questions-library.service';
import { SurveyQuestionSelectionStateService } from '../../services/survey-editor-question-selection-state.service';
import { SurveyEditorService } from '../../services/survey-editor.service';
import { SurveyPositioningService } from '../../services/survey-positioning.service';

@Component({
  selector: 'empathy-editor',
  templateUrl: 'editor.component.html',
  styleUrls: ['./editor.component.scss', './_editor-theme.component.scss'],
  animations: [trigger('tabContent', [transition('* => *', [style({ opacity: 0 }), animate('300ms ease-in-out', style({ opacity: 1 }))])])]
})
export class EditorComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(CommentsListComponent) commentList!: CommentsListComponent;

  @Input() autoSaveInterval = 10000;
  @Input() editorComments: Comment[];
  @Input() previewMode: boolean;
  @Input() selectedActiveDevice: PreviewDevice;
  @Input() surveyEditor: SurveyEditor;
  @Input() projectId: string;
  @Input() researchMethodId: string;
  translationsOnly = input(false);
  canComment = input(true);

  @Output() addComment = new EventEmitter<string>(null);
  @Output() postReply = new EventEmitter<Comment>();
  @Output() save = new EventEmitter<void>();
  @Output() updateComment = new EventEmitter<Comment>();
  @Output() updateCommentsReference = new EventEmitter<void>();

  surveyEditorService = inject(SurveyEditorService);

  activeTab$ = computed(() => this.surveyEditorService.mainTab$());

  commentsFilterValue$ = signal('');
  forceActiveQuestion$ = signal<string>(null);
  forceActiveHiddenQuestion$ = signal<string>(null);
  previewBusy$ = signal(false);
  sourceVariables$ = signal<SourceVariables>({});

  readonly loopSettingsSelection$ = this.surveyEditorService.loopSettingsSelection$;
  blockType = BlockType;
  faChevronsRight = faChevronsRight;
  faClose = faClose;
  faCode = faCode;
  faCodeBranch = faCodeBranch;
  faComment = faComment;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faFileDashedLine = faFileDashedLine;
  faGear = faGear;
  faPlus = faPlus;
  faSquarePollHorizontal = faSquarePollHorizontal;
  mainTabs = MainTabs;
  sideTabs = SideTabs;
  sideHiddenTabs = SideHiddenTabs;
  sideOptionTabs = SideOptionTabs;

  private dialog = inject(MatDialog);
  private editorTranslationsService = inject(EditorTranslationsService);
  private optionsLibraryService = inject(OptionsLibraryService);
  private questionsLibraryService = inject(QuestionsLibraryService);
  private snackbar = inject(MatSnackBar);
  private surveyPositioningService = inject(SurveyPositioningService);
  private surveyQuestionSelectionStateService = inject(SurveyQuestionSelectionStateService);

  private destroyed$ = new Subject<void>();

  constructor() {
    effect(() => this.resetOnTabChange(this.activeTab$()), { allowSignalWrites: true });

    effect(
      () => {
        if (this.translationsOnly()) {
          this.surveyEditorService.mainTab$.set(MainTabs.Translations);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.surveyEditorService.initForm(this.surveyEditor);
  }

  ngAfterViewInit(): void {
    this.surveyEditor.form.valueChanges.pipe(takeUntil(this.destroyed$), debounceTime(1000)).subscribe(() => {
      this.save.emit();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  addPageBreak(): void {
    this.surveyEditorService.addPageBreak(true);
  }

  onAddQuestion(): void {
    const dialogRef = this.dialog.open(QuestionTypePickerComponent);

    dialogRef.afterClosed().subscribe((selection: QuestionType | DemographicsGroup | Question[]) => {
      if (!selection) {
        return;
      }

      if (selection === 'demographics') {
        this.surveyEditorService.addDemographics();
      } else if (Array.isArray(selection)) {
        this.surveyEditorService.copyQuestions(selection);
      } else {
        this.surveyEditorService.addQuestion(selection);
      }

      this.onFilterComments(
        this.surveyEditor.activeQuestion.get('code')?.value
          ? this.surveyEditor.activeQuestion.get('code')?.value
          : this.surveyEditor.activeQuestion.get('id').value
      );
    });
  }

  onAddHiddenQuestion(): void {
    this.surveyEditorService.addHiddenQuestion();
    this.onFilterComments(
      this.surveyEditor.activeHiddenQuestion.get('code')?.value
        ? this.surveyEditor.activeHiddenQuestion.get('code')?.value
        : this.surveyEditor.activeHiddenQuestion.get('id').value
    );
  }

  onAddBlock(): void {
    const dialogRef = this.dialog.open(BlockTypePickerComponent, { data: { showParentBlock: true } });

    dialogRef.afterClosed().subscribe((blockType: BlockType) => {
      if (!blockType) {
        return;
      }

      this.surveyEditorService.surveyEditor$().blocks.push(blockType);
    });
  }

  onRemoveSelectedQuestions(): void {
    const questionIdSet = this.surveyQuestionSelectionStateService.getSelectedQuestionIds();
    this.removeQuestions(questionIdSet);
  }

  onRemoveActiveQuestion(): void {
    this.removeQuestions(new Set<string>([this.surveyEditor.activeQuestion.get('id').value]));
  }

  removeQuestions(questionIdSet: Set<string>): void {
    const { selectedQuestions, affectedQuestions, affectedBlocks } = this.surveyPositioningService.getAllDependenciesOfQuestions(questionIdSet);

    this.dialog
      .open(DeleteQuestionDialogComponent, {
        data: { questionsCount: selectedQuestions.length, affectedQuestions, affectedBlocks }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          selectedQuestions.forEach((q) => this.surveyEditorService.removeQuestionById(q.id));
          if (this.surveyEditorService.surveyEditor$().activeQuestion === null) {
            this.surveyEditorService.selectWelcomeScreen();
          }
        }
      });
  }

  onRemoveActiveHiddenQuestion(): void {
    this.dialog
      .open(DeleteQuestionDialogComponent, {
        data: { questionsCount: 1, affectedQuestions: [], affectedBlocks: [] }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.surveyEditorService.removeActiveHiddenQuestion();
        }
      });
  }

  onMoveSelectedQuestions(): void {
    const questionIdSet = this.surveyQuestionSelectionStateService.getSelectedQuestionIds();

    const { affectedQuestions, affectedBlocks } = this.surveyPositioningService.getAllDependenciesOfQuestions(questionIdSet);

    const affectedElements = !!affectedQuestions?.size || !!affectedBlocks?.size;

    this.dialog.open(MoveBulkToPositionComponent, { data: { questionsToMoveIds: Array.from(questionIdSet), affectedElements } });
  }

  onDuplicateSelectedQuestions(): void {
    const questionIdSet = this.surveyQuestionSelectionStateService.getSelectedQuestionIds();
    const questionsToDuplicate = this.surveyEditorService.surveyEditor$().questions.filter((q) => questionIdSet.has(q.id));

    if (!questionsToDuplicate.length) {
      return;
    }

    const hasConditions = (question: Question): boolean =>
      question.visibleWhen?.length > 0 || question.options?.some((o) => o.visibleWhen?.length > 0) || question.cards?.some((c) => c.visibleWhen?.length > 0);

    this.dialog
      .open(DuplicateQuestionsDialogComponent, {
        data: {
          questionsCount: questionsToDuplicate.length,
          questionsWithConditionsCount: questionsToDuplicate.filter(hasConditions).length,
          activeQuestionCode: this.surveyEditor.activeQuestion?.get('code')?.value,
          hasTranslations: this.editorTranslationsService.localizedTranslations$().length > 0
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.surveyEditorService.duplicateQuestions(questionsToDuplicate);
        }
      });
  }

  onFilterComments(questionId: string): void {
    if (!this.editorComments) {
      return;
    }

    this.commentsFilterValue$.set(questionId ?? '');
  }

  onAddComment(questionId: string): void {
    this.commentList.addNewComment(questionId);
  }

  onSelectQuestionFromComment(id: string): void {
    this.forceActiveQuestion$.set(id.replace('#', ''));
  }

  onSelectHiddenQuestionFromComment(id: string): void {
    this.forceActiveHiddenQuestion$.set(id.replace('#', ''));
  }

  onAddSource(): void {
    const existingSourceTitles = this.surveyEditorService
      .surveyEditor$()
      .sources.editable$()
      .map((s) => s.title);
    const dialogRef = this.dialog.open(SourcePickerComponent, { data: { existingSourceTitles } });

    dialogRef.afterClosed().subscribe((source: string | Source) => {
      if (!source) {
        return;
      }

      if (typeof source === 'string') {
        this.surveyEditorService.surveyEditor$().sources.push(source);
      } else {
        this.surveyEditorService.surveyEditor$().sources.pushModel(source);
      }
    });
  }

  onRemoveSource(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm',
        confirmText: 'Are your sure you want to remove this source? All logic linked with it will be removed too.',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.surveyEditorService.surveyEditor$().sources.removeActive();
      }
    });
  }

  onDuplicateQuestion(): void {
    this.surveyEditorService.copyActiveQuestion();
  }

  onSaveQuestionOnLibrary(): void {
    const questionToSave = this.surveyEditorService.surveyEditor$().activeQuestion.getRawValue();
    this.questionsLibraryService.saveQuestion(questionToSave).subscribe((saved) => {
      if (saved) {
        this.snackbar.open('Question added to the library', 'Ok', {
          duration: 3000,
          panelClass: SnackbarMode.Success
        });
      }
    });
  }

  onSaveOptionsOnLibrary(): void {
    const activeQuestion: Question = this.surveyEditorService.surveyEditor$().activeQuestion.value;
    const optionsToSave: OptionsLibrarySet = {
      name: ToPlainText(activeQuestion.title) ?? '',
      description: ToPlainText(activeQuestion.description) ?? '',
      options: activeQuestion.options.map((opt) => ({ id: opt.id, value: opt.value, text: ToPlainText(opt.text) ?? '' }))
    };

    this.optionsLibraryService.saveOptionsSet(optionsToSave).subscribe((saved) => {
      if (saved) {
        this.snackbar.open('Options added to the library', 'Ok', {
          duration: 3000,
          panelClass: SnackbarMode.Success
        });
      }
    });
  }

  onUpdateHashtagBasedOnQuestionId = (questionId: string): string => {
    let question = this.surveyEditorService.surveyEditor$().questions.find((q) => q.id === questionId.replace('#', ''));
    if (!question) {
      question = this.surveyEditorService.surveyEditor$().questions.find((q) => q.code === questionId.replace('#', ''));
    }

    return question ? `#${question.code}` : questionId;
  };

  onUpdateHashtagBasedOnHiddenQuestionId = (questionId: string): string => {
    let question = this.surveyEditorService.surveyEditor$().questionsHidden.find((q) => q.id === questionId.replace('#', ''));
    if (!question) {
      question = this.surveyEditorService.surveyEditor$().questionsHidden.find((q) => q.code === questionId.replace('#', ''));
    }

    return question ? `#${question.code}` : questionId;
  };

  onUpdateCommentsReference(): void {
    const question = this.surveyEditorService.surveyEditor$().activeQuestion || this.surveyEditorService.surveyEditor$().activeHiddenQuestion;

    this.commentsFilterValue$.set(question.get('code') ? question.get('code').value : question.get('id').value);
    this.updateCommentsReference.emit();
  }

  onOpenMainTabPanel(tab: number): void {
    this.surveyEditorService.activeTab$.set(tab);
    this.surveyEditorService.surveyEditor$().activeOption = null;
    this.surveyEditorService.surveyEditor$().activeCard = null;

    requestAnimationFrame(() => {
      const container = document.getElementsByClassName('right-side-column');
      if (container?.length === 1 && container[0].classList.contains('hide')) {
        container[0].classList.remove('hide');
      }
    });
  }

  onOpenMainTabComments(): void {
    this.addComment.emit(this.surveyEditorService.surveyEditor$().activeQuestion.get('id').value);
    this.onOpenMainTabPanel(SideTabs.Comments);
  }

  onOpenSubTabComments(): void {
    this.addComment.emit(this.surveyEditorService.surveyEditor$().activeHiddenQuestion.get('id').value);
    this.onOpenMainTabPanel(SideHiddenTabs.Comments);
  }

  onToggleRightColumns(): void {
    const container = document.getElementsByClassName('right-side-column');
    if (container?.length === 1) {
      if (container[0].classList.contains('hide')) {
        container[0].classList.remove('hide');
      } else {
        container[0].classList.add('hide');
      }
    }
  }

  private resetOnTabChange(tab: MainTabs): void {
    this.sourceVariables$.set(null);
    if (tab !== MainTabs.FullPreview) {
      this.previewBusy$.set(false);
    }

    switch (tab) {
      case MainTabs.SurveyEditor: {
        this.surveyEditorService.selectWelcomeScreen();
        this.onFilterComments(QuestionPrefix.Question);
        break;
      }
      case MainTabs.HiddenVariables: {
        this.surveyEditorService.selectHiddenQuestion(null);
        this.onFilterComments('');
        break;
      }
    }
  }
}
